import { Badge, Flex, Text, ThemeIcon, useMantineTheme } from '@mantine/core';
import { AuditTct } from '@dealstack/shared/lib/generated';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	formatAuditActionToDisplay,
	formatTableNameToLabel,
} from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import { Dot } from '@dealstack/frontend/shared/components/Audit/AuditLayout/styled.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Fragment } from 'react';
import { ThemeIconProps } from '@mantine/core/lib/components/ThemeIcon/ThemeIcon';

interface Props {
	audit: AuditTct;
	isSelected: boolean;
	onCardClick?: (auditId: string) => void;
	showDivider?: boolean;
	showTableName?: boolean;
	title?: string;
	subtitle?: string;
	singleRow?: boolean;
	iconSize?: ThemeIconProps['size'];
}

export const TimelineAuditCard = ({
	audit,
	isSelected,
	onCardClick,
	showDivider = true,
	showTableName = true,
	title,
	subtitle,
	singleRow = false,
	iconSize = 'xl',
}: Props) => {
	const theme = useMantineTheme();
	const tCapManage = useTranslationCapManage();

	// A row written outside a request has no actor, and "by undefined" is worse than
	// naming the action alone.
	const action = formatAuditActionToDisplay(audit.action, tCapManage);
	const defaultTitle = audit.userDisplayName ? `${action} by ${audit.userDisplayName}` : action;

	const timestamp = formatStringDateToUI(
		audit.createdAt,
		singleRow ? DATE_FORMATS.TIMELINE_EVENT_DATE_TIME : DATE_FORMATS.TIMELINE_DATE_TIME
	);

	// Each detail is introduced by a dot when it shares the title's row, and stands alone
	// on its own line otherwise.
	const details = [timestamp, subtitle].filter((detail): detail is string => !!detail);

	return (
		<Flex direction="column" justify="center" style={{ position: 'relative' }}>
			<Flex gap="0.5rem" align="center">
				<ThemeIcon
					radius="50%"
					color={theme.colors.gray[4]}
					size={iconSize}
					style={{ zIndex: 2, flexShrink: 0 }}
				>
					<Icon variant="checkmark" color="black" />
				</ThemeIcon>

				<Flex
					direction="column"
					gap="0.25rem"
					style={{
						cursor: onCardClick ? 'pointer' : 'unset',
						padding: '0.5rem',
						borderRadius: '6px',
						backgroundColor: isSelected ? theme.colors.blue[0] : 'transparent',
						border: isSelected
							? `1px solid ${theme.colors.blue[4]}`
							: '1px solid transparent',
						width: '100%',
						minWidth: 0,
					}}
					onClick={() => onCardClick?.(audit.id)}
				>
					<Flex gap="0.5rem" align="center" wrap="wrap">
						<Text fw={600} size="sm">
							{title ?? defaultTitle}
						</Text>
						{showTableName && (
							<Badge
								size="sm"
								variant="light"
								color="gray"
								styles={{ inner: { color: theme.colors.dark[0], fontWeight: 500 } }}
								style={{ flexShrink: 0, maxWidth: 'none' }}
							>
								{formatTableNameToLabel(audit.tableName, tCapManage)}
							</Badge>
						)}
						{singleRow &&
							details.map((detail) => (
								<Fragment key={detail}>
									<Dot color={theme.colors.gray[4]} />
									<Text size="sm">{detail}</Text>
								</Fragment>
							))}
					</Flex>

					{!singleRow &&
						details.map((detail) => (
							<Text size="sm" c="gray.9" key={detail}>
								{detail}
							</Text>
						))}
				</Flex>
			</Flex>
			{showDivider && (
				<div
					style={{
						position: 'absolute',
						left: '18px',
						top: '50%',
						height: 'calc(100% + 1.25rem)',
						width: '1px',
						backgroundColor: theme.colors.gray[4],
						zIndex: 1,
					}}
				/>
			)}
		</Flex>
	);
};
